import React, { useState } from 'react';
import { X, Check, ArrowRight, Loader, AlertCircle, RefreshCw, Star } from 'lucide-react';
import { subscriptionService } from '../../services/subscriptionService';
import { formatCurrency } from '../../utils/helpers';

const ChangePlanModal = ({ 
  isOpen, 
  onClose, 
  currentPlan, 
  currentBillingPeriod,
  targetPlan, 
  billingPeriod, 
  isFoundingMember = false,
  hasActiveSubscription = false,
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !targetPlan) return null;

  // Normalize plan names for comparison
  const normalizedCurrentPlan = currentPlan === 'Professional' ? 'Pro' : 
                                 currentPlan === 'Starter' ? 'Solo' : currentPlan;
  const normalizedTargetPlan = targetPlan.name === 'Professional' ? 'Pro' : 
                                targetPlan.name === 'Starter' ? 'Solo' : targetPlan.name;

  // Determine what type of change this is
  const isSamePlan = normalizedCurrentPlan === normalizedTargetPlan;
  const isBillingPeriodChange = isSamePlan && currentBillingPeriod !== billingPeriod;
  
  // Tier comparisons
  const tierOrder = { 'Solo': 1, 'Starter': 1, 'Pro': 2, 'Professional': 2, 'Enterprise': 3 };
  const currentTierLevel = tierOrder[currentPlan] || 1;
  const targetTierLevel = tierOrder[targetPlan.name] || 1;
  
  const isUpgrade = targetTierLevel > currentTierLevel;
  const isDowngrade = targetTierLevel < currentTierLevel;

  // Founder pricing — applies to Pro tier only, monthly only
  const isPro = normalizedTargetPlan === 'Pro';
  const founderRate = 10.00;
  const isFounderPricingApplicable = isFoundingMember && isPro && billingPeriod === 'monthly';

  const standardPrice = billingPeriod === 'monthly' ? targetPlan.monthlyPrice : targetPlan.annualPrice;
  const currentPrice = isFounderPricingApplicable ? founderRate : standardPrice;

  // Filter out features we're temporarily hiding (e.g., QuickBooks sync while it's commented out)
  const displayedFeatures = (targetPlan.features || []).filter(
    feature => !feature.toLowerCase().includes('quickbooks')
  );

  // Determine modal title and messaging
  const getTitle = () => {
    if (isBillingPeriodChange) {
      return billingPeriod === 'annual' ? 'Switch to Annual Billing' : 'Switch to Monthly Billing';
    }
    if (isUpgrade) return `Upgrade to ${targetPlan.name}`;
    if (isDowngrade) return `Downgrade to ${targetPlan.name}`;
    return `Change to ${targetPlan.name}`;
  };

  const getActionText = () => {
    if (!hasActiveSubscription) {
      return `Continue to Checkout`;
    }
    if (isBillingPeriodChange) {
      return billingPeriod === 'annual' ? 'Switch to Annual' : 'Switch to Monthly';
    }
    if (isUpgrade) return 'Confirm Upgrade';
    if (isDowngrade) return 'Confirm Downgrade';
    return 'Confirm Change';
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      setError(null);

      if (hasActiveSubscription) {
        // User has an existing Stripe subscription — swap prices on it
        await subscriptionService.changePlan(targetPlan.name, billingPeriod);
        
        if (onSuccess) {
          onSuccess(targetPlan.name);
        }
        
        onClose();
      } else {
        // First-time subscription — redirect to /subscribe with plan preselected
        // so the user goes through the normal Stripe Checkout flow.
        // The backend's CreateCheckoutSessionAsync will apply founder pricing automatically
        // based on the IsFoundingMember flag on their Business record.
        const planParam = targetPlan.name.toLowerCase();
        window.location.href = `/subscribe?plan=${planParam}`;
      }
    } catch (err) {
      console.error('Error changing plan:', err);
      setError(err.response?.data?.error || err.message || 'Failed to change plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between ${
          isFounderPricingApplicable ? 'bg-amber-50 border-amber-200' :
          isBillingPeriodChange ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            {isFounderPricingApplicable && (
              <div className="bg-amber-500 p-2 rounded-full">
                <Star className="h-5 w-5 text-white fill-white" />
              </div>
            )}
            {isBillingPeriodChange && !isFounderPricingApplicable && (
              <div className="bg-green-100 p-2 rounded-full">
                <RefreshCw className="h-5 w-5 text-green-600" />
              </div>
            )}
            <h2 className="text-2xl font-bold text-gray-900">
              {getTitle()}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Founding Member Pricing Info (takes priority over other messaging) */}
          {isFounderPricingApplicable && (
            <div className="mb-6">
              <div className="bg-gradient-to-br from-amber-50 to-yellow-100 border-2 border-amber-300 rounded-lg p-5 mb-4">
                <div className="flex items-start gap-3 mb-3">
                  <Star className="h-6 w-6 text-amber-600 fill-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-bold text-amber-900 text-base mb-1">
                      Founding Member Rate
                    </h3>
                    <p className="text-sm text-amber-800">
                      You'll be subscribed at your locked founder rate, not the standard price.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Pro Plan — Founder</span>
                    <div className="text-right">
                      <span className="text-lg text-gray-400 line-through mr-2">
                        {formatCurrency(standardPrice)}
                      </span>
                      <span className="text-3xl font-bold text-amber-600">
                        {formatCurrency(currentPrice)}
                      </span>
                      <span className="text-sm font-normal text-gray-600">/mo</span>
                    </div>
                  </div>
                  <p className="text-xs text-amber-700 font-semibold mt-2">
                    🔒 Locked for life — will never increase
                  </p>
                </div>
              </div>

              <h3 className="text-base font-semibold text-gray-900 mb-3">Everything you get:</h3>
              <div className="space-y-2">
                {displayedFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Billing Period Change Info */}
          {isBillingPeriodChange && !isFounderPricingApplicable && (
            <div className="mb-6">
              <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">
                    {targetPlan.name} Plan - {billingPeriod === 'annual' ? 'Annual' : 'Monthly'}
                  </span>
                  <span className="text-2xl font-bold text-green-600">
                    {formatCurrency(currentPrice)}
                    <span className="text-sm font-normal text-gray-600">
                      /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                    </span>
                  </span>
                </div>
                
                {billingPeriod === 'annual' && targetPlan.monthlyPrice && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                    <p className="text-sm font-medium text-green-800">
                      🎉 You'll save ${((targetPlan.monthlyPrice * 12) - targetPlan.annualPrice).toFixed(0)} per year!
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      That's {Math.round((1 - targetPlan.annualPrice / (targetPlan.monthlyPrice * 12)) * 100)}% off compared to monthly billing
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Plan Change Info (Upgrade/Downgrade) - standard pricing path */}
          {!isBillingPeriodChange && !isFounderPricingApplicable && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {isUpgrade ? "What You'll Get" : "Plan Changes"}
              </h3>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">{targetPlan.name} Plan</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {formatCurrency(currentPrice)}
                    <span className="text-sm font-normal text-gray-600">/{billingPeriod === 'monthly' ? 'mo' : 'yr'}</span>
                  </span>
                </div>
                <p className="text-sm text-gray-700">
                  Billed {billingPeriod === 'monthly' ? 'monthly' : 'annually'}
                </p>
              </div>

              <div className="space-y-2">
                {displayedFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Proration Notice - only show if they already have a subscription (not for first-time trial subscribers) */}
          {currentPlan && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-900 font-medium mb-1">
                💳 Proration Applied
              </p>
              <p className="text-sm text-blue-800">
                {isBillingPeriodChange 
                  ? billingPeriod === 'annual'
                    ? "You'll be charged a prorated amount for the annual plan minus any credit from your current period."
                    : "You'll receive a prorated credit for your remaining annual subscription, applied to your monthly billing."
                  : isUpgrade 
                    ? "You'll be charged a prorated amount for the remainder of your billing period. Your next full charge will be on your next billing date."
                    : "You'll receive a prorated credit for the remainder of your billing period. This credit will be applied to your next invoice."
                }
              </p>
            </div>
          )}

          {/* Important Notes for Downgrade */}
          {isDowngrade && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-900 font-medium mb-1">
                ⚠️ Important
              </p>
              <p className="text-sm text-yellow-800">
                Downgrading will limit access to some features. 
                Your data will be preserved but some features will be unavailable.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`flex-1 px-6 py-3 font-semibold rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center ${
                isFounderPricingApplicable
                  ? 'bg-amber-600 text-white hover:bg-amber-700'
                  : isBillingPeriodChange 
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin mr-2 h-5 w-5" />
                  Processing...
                </>
              ) : (
                <>
                  {getActionText()}
                  {isFounderPricingApplicable ? (
                    <Star className="ml-2 h-5 w-5 fill-white" />
                  ) : isBillingPeriodChange ? (
                    <RefreshCw className="ml-2 h-5 w-5" />
                  ) : (
                    <ArrowRight className="ml-2 h-5 w-5" />
                  )}
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePlanModal;